import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import Button from "@/components/ui/Button";
import PhoneAutocomplete from "@/components/ui/PhoneAutocomplete";
import Select from "@/components/ui/Select";
import { createChatInvite } from "@/lib/chat";
import { checkClientPhone, createClient, suggestClients } from "@/lib/clients";
import AppointmentChatModal from "../../appointment-chat/components/AppointmentChatModal";
import SmsComposeModal from "@/components/sms/SmsComposeModal";
import AppointmentPaymentInfo from "./AppointmentPaymentInfo";
import { useAuth } from "@/auth/AuthProvider";

// STATUS_OPTIONS is defined inside the component (needs t())

const EMPTY_FORM = {
  clientId: "",
  clientName: "",
  clientPhone: "",
  serviceIds: [],
  staffId: "",
  date: "",
  time: "",
  duration: "",
  status: "booked",
  notes: "",
};

function formatDateForInput(d) {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseISOOrDate(val) {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val) ? null : val;
  const d = new Date(val);
  return isNaN(d) ? null : d;
}

function normalizeError(e) {
  const data = e?.response?.data;
  if (!data) return e?.message || "A apărut o eroare la salvare.";

  if (typeof data === "string") return data;

  try {
    const parts = [];
    for (const [k, v] of Object.entries(data)) {
      if (Array.isArray(v)) parts.push(`${k}: ${v.join(", ")}`);
      else if (typeof v === "string") parts.push(`${k}: ${v}`);
      else parts.push(`${k}: ${JSON.stringify(v)}`);
    }
    return parts.length ? parts.join(" • ") : "A apărut o eroare la salvare.";
  } catch {
    return "A apărut o eroare la salvare.";
  }
}

export default function AppointmentModal({
  isOpen,
  onClose,
  appointment,
  onSave,
  onDelete,
  mode = "view",
}) {
  const { t } = useTranslation();
  const STATUS_OPTIONS = [
    { value: "booked", label: t("calendar.statusConfirmed"), icon: "🟢" },
    { value: "cancelled", label: t("calendar.statusCancelled"), icon: "🔴" },
    { value: "completed", label: t("calendar.statusCompleted"), icon: "🔵" },
  ];
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [durationAuto, setDurationAuto] = useState(true);

  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [clientSuggestOpen, setClientSuggestOpen] = useState(false);
  const [clientSuggestLoading, setClientSuggestLoading] = useState(false);

  const [phoneCheck, setPhoneCheck] = useState({ phone_normalized: "", matches: [] });
  const [phoneReassignFromId, setPhoneReassignFromId] = useState(null);

  const suggestDebounceRef = useRef(null);
  const phoneDebounceRef = useRef(null);


  const [chatOpen, setChatOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);

  const { permissions } = useAuth();

  const canUseSms = useMemo(() => {
    // dacă ai helper în permissions
    if (typeof permissions?.hasFeature === "function") {
      return !!permissions.hasFeature("sms");
    }

    // fallback: caută sms în orice bucket comun
    const f =
      permissions?.features ||
      permissions?.effective_features ||
      permissions?.plan_features ||
      permissions?.featureFlags ||
      null;

    return !!(f && f.sms);
  }, [permissions]);

  const isEdit = Boolean(appointment && appointment.id);
  const isCreate = !isEdit;
  const isFromSlot = isCreate && appointment && appointment.date && appointment.time;

  const serviceOptions = useMemo(
    () =>
      (Array.isArray(services) ? services : []).map((s) => ({
        value: String(s.id),
        label: s.name || `Serviciu #${s.id}`,
        duration: Number(s.duration) || 30,
      })),
    [services]
  );

  const serviceDurationById = useMemo(() => {
    const map = {};
    for (const s of serviceOptions) map[s.value] = Number(s.duration) || 30;
    return map;
  }, [serviceOptions]);

  const locationIdFromContext = useMemo(() => {
    if (appointment?.locationId) return appointment.locationId;
    if (appointment?._raw?.location_id) return appointment._raw.location_id;
    if (appointment?.location_id) return appointment.location_id;
    try {
      const raw = window.localStorage.getItem("current_location_id");
      return raw ? Number(raw) || null : null;
    } catch {
      return null;
    }
  }, [appointment]);

  // ✅ businessId-ul programării (pentru chat)
  const chatBusinessId = useMemo(() => {
    return (
      appointment?._raw?.business_id ||
      appointment?.business_id ||
      appointment?._raw?.business?.id ||
      appointment?.business?.id ||
      null
    );
  }, [appointment]);

  // încarcă servicii + staff
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setError("");

    const bid = (() => {
      try {
        return window.localStorage.getItem("current_business_id") || null;
      } catch {
        return null;
      }
    })();

    const headers = bid ? { "X-Business-Id": bid } : {};

    const loadServices = async () => {
      try {
        if (!bid) {
          if (!cancelled) setServices([]);
          return;
        }
        const { data } = await api.get(`/b/${bid}/services/`, { headers });
        const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        if (!cancelled) {
          setServices(
            list.map((s) => ({
              id: s.id,
              name: s.name || s.label || `Serviciu #${s.id}`,
              duration: Number(s.duration_minutes) || 30,
            }))
          );
        }
      } catch (e) {
        console.error("Eroare la încărcarea serviciilor:", e?.response?.data || e);
        if (!cancelled) setServices([]);
      }
    };

    const loadStaff = async () => {
      try {
        const { data } = await api.get("/b/staff", { headers });
        if (!cancelled && Array.isArray(data)) {
          setStaff(
            data.map((u) => ({
              id: u.id,
              label:
                (u.first_name || u.last_name
                  ? `${u.first_name || ""} ${u.last_name || ""}`.trim()
                  : u.label || u.username),
            }))
          );
        }
      } catch (e) {
        console.error("Eroare la încărcarea staff-ului:", e?.response?.data || e);
        if (!cancelled) setStaff([]);
      }
    };

    loadServices();
    loadStaff();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // închide chat când se închide modalul principal / se schimbă programarea
  useEffect(() => {
    if (!isOpen) setChatOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setSmsOpen(false);
  }, [isOpen]);

  useEffect(() => {
    setChatOpen(false);
  }, [appointment?.id]);

  useEffect(() => {
    setSmsOpen(false);
  }, [appointment?.id]);

  const smsClientId = useMemo(() => {
    const raw = appointment?._raw || appointment || {};
    return (
      raw.client_id ||
      raw.clientId ||
      raw.client?.id ||
      appointment?.client_id ||
      appointment?.clientId ||
      appointment?.client?.id ||
      null
    );
  }, [appointment]);

  // initializează formularul din appointment
  useEffect(() => {
    if (!isOpen) return;

    const raw = appointment?._raw || appointment || {};
    const start = parseISOOrDate(raw.start_at) || parseISOOrDate(raw.start) || null;
    const end = parseISOOrDate(raw.end_at) || parseISOOrDate(raw.end) || null;

    let duration = "";
    if (start && end && end > start) {
      duration = Math.round((end.getTime() - start.getTime()) / 60000);
    }

    const initialDate = (isFromSlot && appointment.date) || (start ? formatDateForInput(start) : "");
    const initialTime =
      (isFromSlot && appointment.time) ||
      (start
        ? `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`
        : "");

    setError("");

    setForm({
      clientId: String(
        raw.client_id ??
        raw.clientId ??
        (typeof raw.client === "object" ? raw.client?.id : undefined) ??
        ""
      ),
      clientName: raw.clientName || raw.client_name || "",
      clientPhone: raw.clientPhone || raw.client_phone || "",
      serviceIds: (() => {
        const idsFromRaw =
          Array.isArray(raw.service_ids)
            ? raw.service_ids
            : Array.isArray(raw.serviceIds)
              ? raw.serviceIds
              : Array.isArray(raw.services)
                ? raw.services
                : [];

        const normalized = idsFromRaw
          .map((x) => String(x))
          .filter((x) => x && x !== "null" && x !== "undefined");

        if (normalized.length) return normalized;

        const one =
          raw.serviceId ||
          raw.service_id ||
          (typeof raw.service === "number" ? raw.service : raw.service?.id) ||
          "";
        return one ? [String(one)] : [];
      })(),
      staffId: String(
        raw.staffId ??
        raw.staff_id ??
        raw.staff_user_id ??
        (typeof raw.staff === "object" ? raw.staff?.id : raw.staff) ??
        ""
      ),

      date: initialDate,
      time: initialTime,
      duration: duration ? String(duration) : "",
      status: raw.status || "booked",
      notes: raw.notes || "",
    });
    setDurationAuto(isCreate);
  }, [isOpen, appointment, isFromSlot]);

  

  // --- Client autocomplete (name) ---
  useEffect(() => {
    if (!isOpen) return;

    const q = String(form.clientName || "").trim();
    if (form.clientId) {
      setClientSuggestions([]);
      return;
    }
    if (q.length < 2) {
      setClientSuggestions([]);
      return;
    }

    if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
    suggestDebounceRef.current = setTimeout(async () => {
      try {
        setClientSuggestLoading(true);
        const rows = await suggestClients(q, { limit: 8 });
        setClientSuggestions(Array.isArray(rows) ? rows : []);
      } catch {
        setClientSuggestions([]);
      } finally {
        setClientSuggestLoading(false);
      }
    }, 250);

    return () => {
      if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
    };
  }, [isOpen, form.clientName, form.clientId]);

  // --- Phone conflict check (only when creating / not using an existing client) ---
  useEffect(() => {
    if (!isOpen) return;

    if (form.clientId) {
      setPhoneCheck({ phone_normalized: "", matches: [] });
      setPhoneReassignFromId(null);
      return;
    }

    const p = String(form.clientPhone || "").trim();
    if (p.length < 3) {
      setPhoneCheck({ phone_normalized: "", matches: [] });
      setPhoneReassignFromId(null);
      return;
    }

    if (phoneDebounceRef.current) clearTimeout(phoneDebounceRef.current);
    phoneDebounceRef.current = setTimeout(async () => {
      try {
        const data = await checkClientPhone(p);
        setPhoneCheck(data || { phone_normalized: "", matches: [] });

        const ids = new Set((data?.matches || []).map((m) => m?.id));
        if (phoneReassignFromId && !ids.has(phoneReassignFromId)) {
          setPhoneReassignFromId(null);
        }
      } catch {
        setPhoneCheck({ phone_normalized: "", matches: [] });
      }
    }, 250);

    return () => {
      if (phoneDebounceRef.current) clearTimeout(phoneDebounceRef.current);
    };
  }, [isOpen, form.clientPhone, form.clientId, phoneReassignFromId]);

// Auto-calc duration din serviciile selectate (mai ales la creare)
  useEffect(() => {
    if (!isOpen) return;
    if (!durationAuto) return;
    const ids = Array.isArray(form.serviceIds) ? form.serviceIds : [];
    if (!ids.length) return;
    const dur = ids
      .map((id) => serviceDurationById[String(id)] ?? 30)
      .reduce((a, b) => a + b, 0);
    if (!dur) return;
    setForm((prev) => ({ ...prev, duration: String(dur) }));
  }, [isOpen, durationAuto, form.serviceIds, serviceDurationById]);

  const handleChange = (field) => (e) => {
    const value = e?.target?.value ?? "";

    setForm((prev) => {
      const next = { ...prev, [field]: value };

      // dacă userul începe să scrie peste un client selectat, resetăm selecția
      if ((field === "clientName" || field === "clientPhone") && prev.clientId) {
        next.clientId = "";
      }

      return next;
    });

    if (field === "duration") setDurationAuto(false);

    // dacă schimbă telefonul, resetăm decizia de re-atribuire și re-check
    if (field === "clientPhone") {
      setPhoneReassignFromId(null);
      setPhoneCheck({ phone_normalized: "", matches: [] });
    }

    if (error) setError("");
  };

  const pickExistingClient = (c) => {
    if (!c?.id) return;
    setForm((prev) => ({
      ...prev,
      clientId: String(c.id),
      clientName: c.name || prev.clientName,
      clientPhone: c.phone || prev.clientPhone,
    }));
    setClientSuggestions([]);
    setClientSuggestOpen(false);
    setPhoneCheck({ phone_normalized: "", matches: [] });
    setPhoneReassignFromId(null);
    if (error) setError("");
  };

  const clearPickedClient = () => {
    setForm((prev) => ({ ...prev, clientId: "" }));
    setPhoneCheck({ phone_normalized: "", matches: [] });
    setPhoneReassignFromId(null);
  };

  const validateBeforeSave = () => {
  const problems = [];

  const hasSelectedClient = !!form.clientId;

  if (!form.clientName.trim()) problems.push("Numele clientului este obligatoriu.");
  if (isCreate && !hasSelectedClient && !form.clientPhone.trim())
    problems.push("Telefonul este obligatoriu la creare (dacă nu alegi un client existent).");

  if (!hasSelectedClient && form.clientPhone.trim()) {
    const digitsOnly = form.clientPhone.replace(/\D/g, "");
    if (digitsOnly.length < 6 || digitsOnly.length > 15)
      problems.push("Numărul de telefon trebuie să aibă între 6 și 15 cifre.");
  }

  // dacă telefonul e deja folosit și nu ai ales client existent / reatribuire
  const hasPhoneConflict =
    !hasSelectedClient && Array.isArray(phoneCheck?.matches) && phoneCheck.matches.length > 0;

  if (hasPhoneConflict && !phoneReassignFromId) {
    problems.push(
      "Telefonul este deja folosit. Alege: folosește clientul existent, mută numărul la client nou, sau schimbă numărul."
    );
  }

  if (!Array.isArray(form.serviceIds) || form.serviceIds.length === 0)
    problems.push("Alege cel puțin un serviciu.");
  if (!form.staffId) problems.push("Alege personal.");
  if (!isFromSlot) {
    if (!form.date) problems.push("Data este obligatorie.");
    if (!form.time) problems.push("Ora este obligatorie.");
  }
  if (!locationIdFromContext) problems.push("Nu este selectată o sucursală (location).");

  return problems;
};

  const handleSubmit = async (e) => {
  e?.preventDefault?.();
  if (!onSave) return;

  const problems = validateBeforeSave();
  if (problems.length) {
    setError(problems.join(" • "));
    return;
  }

  setSaving(true);
  setError("");

  try {
    let clientIdToUse = form.clientId ? Number(form.clientId) : null;

    // Dacă userul a ales "mută numărul la client nou" -> creăm clientul aici (în același pas)
    if (!clientIdToUse && phoneReassignFromId) {
      const createdClient = await createClient({
        name: String(form.clientName || "").trim(),
        phone: String(form.clientPhone || "").trim(),
        forcePhoneReassign: true,
        fromClientId: Number(phoneReassignFromId),
      });

      clientIdToUse = createdClient?.id ? Number(createdClient.id) : null;

      if (clientIdToUse) {
        setForm((prev) => ({ ...prev, clientId: String(clientIdToUse) }));
        setPhoneReassignFromId(null);
        setPhoneCheck({ phone_normalized: "", matches: [] });
      }
    }

    const payload = {
      id: appointment?.id,
      clientId: clientIdToUse || undefined,
      clientName: form.clientName.trim(),
      clientPhone: form.clientPhone.trim(),
      serviceIds: (Array.isArray(form.serviceIds) ? form.serviceIds : [])
        .map((x) => Number(x))
        .filter((n) => Number.isFinite(n)),
      staffId: form.staffId ? Number(form.staffId) : undefined,
      date: isFromSlot ? appointment.date : form.date,
      time: isFromSlot ? appointment.time : form.time,
      duration: form.duration ? Number(form.duration) : undefined,
      status: form.status,
      notes: form.notes,
      locationId: locationIdFromContext || undefined,
    };

    await onSave(payload);
  } catch (e2) {
    console.error("Eroare la salvare programare:", e2?.response?.data || e2);
    setError(normalizeError(e2));
  } finally {
    setSaving(false);
  }
};

  const handleDelete = async () => {
    if (!onDelete || !appointment?.id) return;
    if (!window.confirm("Sigur vrei să ștergi această programare?")) return;
    await onDelete(appointment.id);
  };

  const handleOpenChat = async () => {
    if (!appointment?.id) return;

    try {
      // ✅ dacă lib/chat.js suportă businessId la invite, trimite-l
      await createChatInvite(appointment.id, { sendSms: false }, { businessId: chatBusinessId });
    } catch {
      // ok
    }

    setChatOpen(true);
  };

  const handleOpenSms = () => {
    setSmsOpen(true);
  };

  if (!isOpen) return null;

  const title = isEdit ? t("calendar.appointmentDetails") : t("appointments.newTitle");
  const isConfirmedBySms = isEdit && appointment?.confirmed_at;

  return (
    <>
      <div className="fixed inset-0 z-[200] flex justify-center items-end sm:items-center bg-black/40 backdrop-blur-sm p-0 sm:p-4">
        <div className="w-full bg-surface text-foreground shadow-xl flex flex-col h-[100dvh] sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">{title}</h2>
              {isConfirmedBySms && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Confirmată de client
                </span>
              )}
            </div>
            <button type="button" className="text-gray-500 hover:text-gray-800" onClick={onClose}>
              ✕
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
            {error ? (
              <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            {/* ... restul formularului EXACT cum îl ai ... */}

            {/* Informații client */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-3">{t("appointments.clientInfo")}</h3>

              {form.clientId ? (
                <div className="mb-3 rounded border border-border bg-input px-3 py-2 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <span className="font-medium">Client existent selectat:</span>{" "}
                    <span className="font-semibold">{form.clientName || "(fără nume)"}</span>
                    {form.clientPhone ? <span className="text-muted-foreground"> • {form.clientPhone}</span> : null}
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="text-xs underline text-foreground/80 hover:text-foreground"
                      onClick={() =>
                        navigate(`/app/client-management?editClientId=${encodeURIComponent(form.clientId)}`)
                      }
                    >
                      Deschide
                    </button>
                    <button
                      type="button"
                      className="text-xs underline text-foreground/80 hover:text-foreground"
                      onClick={clearPickedClient}
                    >
                      Schimbă
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{t("appointments.clientNameLabel")} *</label>
                  <input
                    type="text"
                    className="w-full rounded border border-border bg-input text-foreground px-3 py-2 text-sm"
                    placeholder={t("appointments.clientNamePlaceholderModal")}
                    value={form.clientName}
                    onChange={handleChange("clientName")}
                    onFocus={() => setClientSuggestOpen(true)}
                    onBlur={() => setTimeout(() => setClientSuggestOpen(false), 150)}
                    required
                  />

                  {clientSuggestOpen && !form.clientId && String(form.clientName || "").trim().length >= 2 ? (
                    <div className="absolute z-[300] mt-1 w-full rounded border border-border bg-surface shadow-lg max-h-64 overflow-auto">
                      {clientSuggestLoading ? (
                        <div className="px-3 py-2 text-xs text-muted-foreground">Caut…</div>
                      ) : clientSuggestions.length ? (
                        clientSuggestions.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickExistingClient(c)}
                            className="w-full text-left px-3 py-2 hover:bg-muted"
                          >
                            <div className="text-sm font-medium">
                              {c.name}
                              {c.is_vip ? <span className="ml-2 text-xs">⭐ VIP</span> : null}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {(c.phone || "").trim() ? `📞 ${c.phone}` : ""}
                              {(c.email || "").trim() ? `  •  ✉️ ${c.email}` : ""}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-xs text-muted-foreground">Niciun client găsit.</div>
                      )}
                    </div>
                  ) : null}
                </div>
                <div>
                  <PhoneAutocomplete
                    label={t("common.phone")}
                    labelClassName="text-xs text-muted-foreground"
                    placeholder="07xx xxx xxx"
                    value={form.clientPhone}
                    onChange={handleChange("clientPhone")}
                    onPickClient={!form.clientId ? pickExistingClient : undefined}
                    required={isCreate && !form.clientId}
                  />

                  {!form.clientId && Array.isArray(phoneCheck?.matches) && phoneCheck.matches.length ? (
                    <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      <div className="font-semibold mb-1">Atenție: numărul este deja în baza de date.</div>
                      <div className="text-amber-800 mb-2">
                        {phoneCheck.matches.length === 1 ? (
                          <>
                            Numărul e alocat lui <b>{phoneCheck.matches[0]?.name}</b>. Vrei să-l folosești pe {""}
                            <b>{phoneCheck.matches[0]?.name}</b> sau să-l muți la client nou?
                          </>
                        ) : (
                          <>Numărul e alocat unor clienți. Vrei să folosești unul existent sau să-l muți la client nou?</>
                        )}
                      </div>

                      <div className="space-y-2">
                        {phoneCheck.matches.map((m) => (
                          <div key={m.id} className="flex items-start justify-between gap-3 rounded border border-amber-100 bg-white/40 px-2 py-2">
                            <div>
                              <div className="font-medium">{m.name}</div>
                              <div className="text-amber-800">
                                {(m.phone || "").trim() ? `📞 ${m.phone}` : ""}
                                {(m.email || "").trim() ? `  •  ✉️ ${m.email}` : ""}
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <button
                                type="button"
                                className="text-xs underline"
                                onClick={() => pickExistingClient(m)}
                              >
                                Folosește clientul
                              </button>

                              <button
                                type="button"
                                className="text-xs underline"
                                onClick={() => {
                                  const ok = window.confirm(
                                    `Numărul e alocat lui ${m.name}.\n\nVrei să-l muți la client nou?\n\n(La salvare: vechiului client i se șterge numărul, iar clientul nou îl primește.)`
                                  );
                                  if (!ok) return;
                                  setPhoneReassignFromId(m.id);
                                  if (error) setError("");
                                }}
                              >
                                Mută numărul la client nou
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {phoneReassignFromId ? (
                        <div className="mt-2 text-amber-800">
                          ✅ La salvare, numărul va fi mutat la clientul nou.
                          <button
                            type="button"
                            className="ml-2 text-xs underline"
                            onClick={() => setPhoneReassignFromId(null)}
                          >
                            Renunță
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Serviciu & angajat */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-3">{t("appointments.serviceStaffSection")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Select
                    label={`${t("appointments.servicesSection")} *`}
                    options={serviceOptions}
                    multiple
                    searchable
                    placeholder={t("appointments.chooseServices")}
                    value={form.serviceIds}
                    onChange={(val) => {
                      setForm((prev) => ({ ...prev, serviceIds: val }));
                      if (error) setError("");
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{t("appointments.staffLabel")} *</label>
                  <select
                    className="w-full rounded border border-border px-3 py-2 text-sm bg-input text-foreground"
                    value={form.staffId}
                    onChange={handleChange("staffId")}
                    required
                  >
                    <option value="">{t("appointments.chooseStaff")}</option>
                    {staff.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Programare */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-3">{t("appointments.schedulingSection")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr] gap-4 items-end">
                {isFromSlot ? (
                  <div className="md:col-span-2">
                    <div className="text-xs font-medium text-muted-foreground mb-1">{t("appointments.dateTimeLabel")}</div>
                    <div className="px-3 py-2 rounded border border-border bg-input text-foreground text-sm">
                      {appointment.date} • {appointment.time}
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Data *</label>
                      <input
                        type="date"
                        className="w-full rounded border border-border bg-input text-foreground px-3 py-2 text-sm dark:[color-scheme:dark]"
                        value={form.date}
                        onChange={handleChange("date")}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Ora *</label>
                      <input
                        type="time"
                        className="w-full rounded border border-border bg-input text-foreground px-3 py-2 text-sm dark:[color-scheme:dark]"
                        value={form.time}
                        onChange={handleChange("time")}
                        required
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{t("appointments.durationLabel")}</label>
                  <input
                    type="number"
                    className="w-full rounded border border-border bg-input text-foreground px-3 py-2 text-sm"
                    placeholder={t("appointments.durationPlaceholder")}
                    min={5}
                    step={5}
                    value={form.duration}
                    onChange={handleChange("duration")}
                  />
                </div>
              </div>
            </div>

            {/* Status & Observații */}
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{t("common.status")}</label>
                  <select
                    className="w-full rounded border border-border px-3 py-2 text-sm bg-input text-foreground"
                    value={form.status}
                    onChange={handleChange("status")}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}{"\u00A0"}
                        {s.icon}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{t("appointments.notesSection")}</label>
                  <textarea
                    rows={3}
                    className="w-full rounded border border-border bg-input text-foreground px-3 py-2 text-sm resize-none"
                    placeholder={t("appointments.notesPlaceholderModal")}
                    value={form.notes}
                    onChange={handleChange("notes")}
                  />
                </div>
              </div>
            </div>

            {/* Payment Info (only when editing existing appointment) */}
            {isEdit && appointment && (
              <AppointmentPaymentInfo appointment={appointment} />
            )}
          </form>

          {/* Footer */}
          <div className="border-t px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="order-2 sm:order-1">
              {isEdit && onDelete && (
                <button
                  type="button"
                  className="text-sm text-red-600 hover:text-red-700"
                  onClick={handleDelete}
                >
                  {t("calendar.deleteAppointment")}
                </button>
              )}
            </div>

            <div className="order-1 sm:order-2 flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
              {isEdit && (
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={handleOpenChat}
                  disabled={saving}
                >
                  {t("appointments.openChat")}
                </Button>
              )}

              {isEdit && canUseSms && (
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={handleOpenSms}
                  disabled={saving}
                >
                  {t("appointments.sendSms")}
                </Button>
              )}

              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={onClose}
                disabled={saving}
              >
                {t("common.cancel")}
              </Button>

              <Button
                type="button"
                variant="default"
                fullWidth
                loading={saving}
                onClick={handleSubmit}
              >
                {t("common.save")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Chat Modal (corect) */}
      <AppointmentChatModal
        isOpen={chatOpen}
        appointmentId={appointment?.id}
        businessId={chatBusinessId}
        onClose={() => setChatOpen(false)}
      />

      {canUseSms && (
        <SmsComposeModal
          isOpen={smsOpen}
          onClose={() => setSmsOpen(false)}
          appointmentId={appointment?.id}
          clientId={smsClientId}
          defaultPhone={form?.clientPhone || appointment?._raw?.client_phone || ""}
          defaultBody={""}
        />
      )}
    </>
  );
}
